import ParkingContext from 'context/ParkingContext';
import useEVChargingPricingInfo from 'hooks/useEvChargingPricingInfo';
import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChargingStation } from 'types/generatedSchemaTypes';

export const useParkingButtonLogic = () => {
  const { t } = useTranslation();
  const { parkingZoneResult, mySalesOrdersForZone } =
    useContext(ParkingContext);

  const { uid = '', parkingMethods } = parkingZoneResult?.data?.parkZone || {};
  const isUnmanagedParking = parkingMethods?.unmanagedParking === true;
  const isAnpr = parkingMethods?.anpr === true;
  const { pricingSchemeId, pricingInfoLoading } = useParkingPricingInfo(
    uid,
    true,
    isUnmanagedParking
  );

  // The access gate reads EV charging pricing, not parking pricing: parking
  // pricing returns nothing for these zones, so it cannot tell a zone with
  // public chargers apart from one whose chargers are all private.
  const chargingStations = useMemo(
    () =>
      (
        parkingZoneResult?.data?.parkZone?.parkingZoneChargingStations ?? []
      ).filter((station): station is ChargingStation => !!station),
    [parkingZoneResult?.data?.parkZone?.parkingZoneChargingStations]
  );
  const {
    isPrivateOnlyWithNoAccess: evPricingIsPrivateOnlyWithNoAccess,
    EVChargingPricingsLoading,
  } = useEVChargingPricingInfo({ chargingStations });
  // ANPR parking is camera-based, so a missing STP scheme does not mean the
  // zone has no parking. Treat it as available so the Park Only action stays
  // on screen next to charging (including when chargers are private).
  const parkingWithApp = !!pricingSchemeId || isAnpr;
  const hasSalesOrder = !!mySalesOrdersForZone?.length;
  const userPaysParking = useMemo(
    () =>
      hasSalesOrder
        ? mySalesOrdersForZone.some(
            (salesOrder) =>
              salesOrder?.orderLines?.[0]?.pmc?.doShortTermPriceCalculation
          )
        : true,
    [hasSalesOrder, mySalesOrdersForZone]
  );

  // True only when every charging segment on the zone is private and the user
  // has no permit granting access to any of them. A zone with a public
  // charging segment or with no charging pricing at all, is not gated. The
  // permit check is already folded in: `availableSegments` filters out private
  // segments the user's permits do not unlock.
  const userLacksPrivateEvPriceAccess = evPricingIsPrivateOnlyWithNoAccess;

  const buttonText = useMemo(() => {
    if (!parkingWithApp && !userPaysParking) {
      return t('button.permitActive');
    }
    if (!parkingWithApp) {
      return t('parking.notSupported');
    }
    return t('button.parkOnly');
  }, [parkingWithApp, userPaysParking, t]);
  return {
    buttonText: buttonText,
    disabled: !parkingWithApp,
    userLacksPrivateEvPriceAccess,
    // `disabled` comes from the pricing query; while it loads, `disabled` means
    // "not loaded yet", not "no parking" wait for this before acting.
    // EV charging pricing is included because `userLacksPrivateEvPriceAccess`
    // is false until it resolves, which would otherwise let a gated zone
    // briefly render as accessible.
    isPricingLoading: pricingInfoLoading || EVChargingPricingsLoading,
  };
};
